/**
 * @file 問題ごとの解説の面 — 本だなの資料から、対応する作品の問題一覧と解説を読む。
 *
 * @what: /study/:familyId/sources/:sourceId/guides/:problemId?。資料の work_id で共通データ
 *   (GET /content/study_works/{workId}) を引き、選んだ問題の解説を1件ずつ取る。問題 id が
 *   URL に無ければ、解説のある最初の問題を選ぶ。選択は URL に載せる (リーダーとの往復のため)。
 * @why: 解説は家族に依存しない共通データで、資料は家族の持ち物である。2つを結ぶのは資料の work_id と
 *   reader_offset だけ (問題ごとの解説の設計 §3.5・§6.1)。
 * @failure: 資料に work_id が無ければ1行で知らせる。共通データの404 (未配信・backend 未デプロイ)
 *   は「解説を読み込めませんでした」に倒し、他の面に影響させない。
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchStudyProblemGuide, type StudyProblemGuide } from "@/features/study/api/studyWorks";
import { LearnerTabs } from "@/features/study/components/LearnerTabs";
import { AskRecordPanel } from "@/features/study/guides/AskRecordPanel";
import { ProblemGuideView, type GuideState } from "@/features/study/guides/ProblemGuideView";
import { readerPageOf } from "@/features/study/model/sourcePages";
import { useLearnerSelection } from "@/features/study/hooks/useLearnerSelection";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import { useStudySources } from "@/features/study/hooks/useStudySources";
import { useStudyWork } from "@/features/study/hooks/useStudyWork";
import "@/features/study/components/study.css";

export function StudyGuidesRoute() {
  const { familyId = "", sourceId = "", problemId } = useParams();
  const navigate = useNavigate();
  useStudyBackground();
  const sourcesState = useStudySources(familyId);
  const source =
    sourcesState.status === "ready"
      ? sourcesState.sources.find((s) => s.id === sourceId)
      : undefined;
  const workId = source?.work_id ?? "";
  const readerOffset = source?.reader_offset ?? 0;
  const readerHref = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}`;

  const work = useStudyWork(workId);
  const index = work.status === "ready" ? work.index : undefined;

  // ふりかえりはまなぶ人ごと。リーダーと同じく端末が覚えている人を既定にし、ヘッダーにも出す。
  const { learners, learnerId, selectLearner } = useLearnerSelection(familyId);
  // URL に問題 id が無ければ、解説のある最初の問題 (無ければ最初の問題) を選ぶ。
  const selectedId = useMemo(() => {
    if (!index) return null;
    if (problemId && index.problems.some((p) => p.id === problemId)) return problemId;
    return (index.problems.find((p) => p.has_guide) ?? index.problems[0])?.id ?? null;
  }, [index, problemId]);

  // 問が載るページ (リーダーの連番)。問そのものに紐づかない記録も、同じページなら拾うために渡す。
  const selectedPage = useMemo(() => {
    const problem = index?.problems.find((p) => p.id === selectedId);
    if (!problem || problem.page <= 0) return null;
    return readerPageOf({ reader_offset: readerOffset }, problem.page);
  }, [index, selectedId, readerOffset]);

  const [guides, setGuides] = useState<Record<string, GuideState>>({});
  useEffect(() => {
    if (!index || !selectedId) return;
    const problem = index.problems.find((p) => p.id === selectedId);
    if (!problem?.has_guide || guides[selectedId]) return;
    const controller = new AbortController();
    setGuides((g) => ({ ...g, [selectedId]: { status: "loading" } }));
    fetchStudyProblemGuide(index.work_id, selectedId, controller.signal)
      .then((guide: StudyProblemGuide) => {
        if (!controller.signal.aborted)
          setGuides((g) => ({ ...g, [selectedId]: { status: "ready", guide } }));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load study guide", err);
        setGuides((g) => ({ ...g, [selectedId]: { status: "error" } }));
      });
    return () => controller.abort();
    // guides を依存に入れると取得のたびに再実行されるので、選択と一覧だけを見る。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, selectedId]);

  const onSelect = (id: string) => {
    navigate(`${readerHref}/guides/${encodeURIComponent(id)}`);
  };

  return (
    <div className="study-scope study-page">
      <p className="study-aid-back">
        <Link to={readerHref}>← 本のページに戻る</Link>
      </p>
      <h1 className="study-aid-title">問題ごとの解説</h1>
      <p className="study-aid-tagline">
        {source
          ? `『${source.title}』の例題と CHECK! を一覧し、解き方の骨組み・別解・背景を読めます`
          : ""}
      </p>
      {sourcesState.status === "error" ? (
        <p role="alert" className="study-error">
          資料の情報を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      ) : sourcesState.status === "loading" ? (
        <p role="status" className="study-muted">
          読み込み中...
        </p>
      ) : !source ? (
        <p role="alert" className="study-error">
          この資料は見つかりません。
          <Link to={`/study/${encodeURIComponent(familyId)}`}>本だなへ戻る</Link>
        </p>
      ) : !workId ? (
        <p className="study-muted">この資料には、問題ごとの解説がまだ結びついていません。</p>
      ) : work.status === "error" ? (
        <p role="alert" className="study-error">
          解説を読み込めませんでした。
        </p>
      ) : index ? (
        <ProblemGuideView
          index={index}
          readerHref={readerHref}
          readerOffset={readerOffset}
          selectedId={selectedId}
          onSelect={onSelect}
          guide={(selectedId && guides[selectedId]) || { status: "none" }}
          below={
            selectedId && learnerId ? (
              <>
                {learners.length > 1 ? (
                  <LearnerTabs
                    learners={learners}
                    selectedId={learnerId}
                    onSelect={selectLearner}
                  />
                ) : null}
                <AskRecordPanel
                  familyId={familyId}
                  personId={learnerId}
                  sourceId={sourceId}
                  problemIds={[selectedId]}
                  showScope
                  {...(selectedPage !== null ? { page: selectedPage } : {})}
                />
              </>
            ) : null
          }
        />
      ) : (
        <p role="status" className="study-muted">
          読み込み中...
        </p>
      )}
    </div>
  );
}
